import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

// Actions
import { parseFetchOptions } from "src/state/user/actions";

// Config
import {
  getRecommendedRecipientUrl,
  getShareWithRecipientsUrl
} from "src/config/apiConfig";
import { getRoute, urls } from "src/config/routes";
import colors from "src/config/colors";

// Hooks
import { useAppInsights } from "src/utils/telemetry/AppInsights";

// Utils
import isCaseSubmittedTo from "src/utils/isCaseSubmittedTo";

// Components
import ShareWithOptionItem from "src/containers/ShareWithPage/ShareWithOptionItem";
import ContentCardHeader from "../ContentCardHeader";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "transparent",
    border: `1px solid ${colors.mystic}`,
    display: "flex",
    flexDirection: "column"
  },
  title: {
    fontSize: 22
  },
  listWrapper: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3, 4)
  },
  list: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    margin: 0,
    listStyle: "none"
  }
}));

// Text isn't in Localazy yet, so it's hardcoded for now (see task 5083) - swap
// for real translation keys once copy is ready there.
const CaseSharingCard = ({ data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { trackException } = useAppInsights();
  const [options, setOptions] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState(null);
  const [isLoadingRecommended, setLoadingRecommended] = useState(true);

  useEffect(() => {
    if (!data.incident?.location) {
      setLoading(false);
      return;
    }

    setLoading(true);
    dispatch(
      parseFetchOptions(({ headers }) => {
        fetch(getShareWithRecipientsUrl(data.id), { headers })
          .then(res => (res.ok ? res.json() : []))
          .then(setOptions)
          .catch(() => {
            trackException(
              `Failed to get share with recipients from case ${data.id}`
            );
          })
          .finally(() => setLoading(false));
      })
    );
  }, [data.id, data.incident, dispatch, trackException]);

  useEffect(() => {
    // Suggesting a recipient only makes sense for the case owner - a
    // collaborator/invitee can't submit the case to anyone, so there's
    // nothing for them to act on here.
    if (!data.isOwnedByUser) {
      setLoadingRecommended(false);
      return;
    }

    setLoadingRecommended(true);
    dispatch(
      parseFetchOptions(({ headers }) => {
        fetch(getRecommendedRecipientUrl(data.id), { headers })
          .then(res => (res.status === 204 ? null : res.json()))
          .then(setRecommended)
          .catch(() => {
            trackException(
              `Failed to get recommended recipient from case ${data.id}`
            );
          })
          .finally(() => setLoadingRecommended(false));
      })
    );
  }, [data.id, data.isOwnedByUser, dispatch, trackException]);

  if (isLoading || isLoadingRecommended) return null;

  const sharedWith = options.filter(isCaseSubmittedTo);
  const suggested = data.isOwnedByUser && recommended ? [recommended] : [];

  if (sharedWith.length === 0 && suggested.length === 0) return null;

  const shareWithUrl = getRoute(urls.shareWith, { caseId: data.id });

  return (
    <Paper elevation={0} className={classes.root}>
      {suggested.length > 0 && (
        <>
          <ContentCardHeader>
            <Text
              className={classes.title}
              type="heading2"
              text="caseMenu.caseSummary.suggestSharing.title"
            />
          </ContentCardHeader>
          <div className={classes.listWrapper}>
            <ul className={classes.list}>
              {suggested.map(item => (
                <ShareWithOptionItem
                  key={item.id}
                  item={item}
                  to={shareWithUrl}
                  isOwnedByUser={data.isOwnedByUser}
                  onReferenceSaved={newValue =>
                    setRecommended(prev => ({
                      ...prev,
                      submission: { ...prev.submission, referenceNumber: newValue }
                    }))
                  }
                />
              ))}
            </ul>
          </div>
        </>
      )}
      {sharedWith.length > 0 && (
        <>
          <ContentCardHeader>
            <Text
              className={classes.title}
              type="heading2"
              text="caseMenu.caseSummary.sharedWith.title"
            />
          </ContentCardHeader>
          <div className={classes.listWrapper}>
            <ul className={classes.list}>
              {sharedWith.map(item => (
                <ShareWithOptionItem
                  key={item.id}
                  item={item}
                  to={shareWithUrl}
                />
              ))}
            </ul>
          </div>
        </>
      )}
    </Paper>
  );
};

CaseSharingCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    isOwnedByUser: PropTypes.bool,
    incident: PropTypes.shape({
      location: PropTypes.shape({})
    })
  }).isRequired
};

export default CaseSharingCard;
